import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import GlobalHeader from '../components/GlobalHeader';
import TemplateTable from '../components/templates/TemplateTable';
import TemplateThumbnail from '../components/templates/TemplateThumbnail';
import TemplateVersionHistoryModal from '../components/templates/TemplateVersionHistoryModal';
import TemplateUsagesModal from '../components/templates/TemplateUsagesModal';
import './TemplateLibraryPage.css';

function TemplateLibraryPage() {
    const navigate = useNavigate();
    const { canCreate, canUpdate, canDelete } = usePermissions();
    const [templates, setTemplates] = useState([]);
    const [filteredTemplates, setFilteredTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState(null);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [viewMode, setViewMode] = useState(() => {
        return localStorage.getItem('template_view_mode') || 'grid';
    });
    const [showVersionHistory, setShowVersionHistory] = useState(false);
    const [selectedTemplateForHistory, setSelectedTemplateForHistory] = useState(null);
    const [showUsagesModal, setShowUsagesModal] = useState(false);
    const [selectedTemplateForUsages, setSelectedTemplateForUsages] = useState(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        loadTemplates();
    }, []);

    useEffect(() => {
        filterTemplates();
    }, [templates, searchTerm]);

    // Auto-effacer l'erreur après 5 secondes
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    const loadTemplates = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await API.getTemplates();
            if (response && response.success) {
                setTemplates(response.data.templates);
            }
        } catch (err) {
            setError('Erreur lors du chargement des modèles');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const filterTemplates = () => {
        let filtered = [...templates];

        // Afficher tous les modèles de l'organisation (plus seulement ceux de l'utilisateur)
        // filtered = filtered.filter(tpl => tpl.is_owner); // SUPPRIMÉ pour le partage

        // Filtre par recherche
        if (searchTerm) {
            filtered = filtered.filter(tpl =>
                tpl.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (tpl.description && tpl.description.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        setFilteredTemplates(filtered);
    };

    const handleCreateNew = () => {
        navigate('/templates/new');
    };

    const handleEdit = (id) => {
        navigate(`/templates/edit/${id}`);
    };

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        localStorage.setItem('template_view_mode', mode);
    };

    const handleDuplicate = async (id) => {
        try {
            const response = await API.duplicateTemplate(id);
            if (response && response.success) {
                await loadTemplates();
                // Message succès
            }
        } catch (err) {
            setError('Erreur lors de la duplication');
            console.error(err);
        }
    };

    const handleDeleteClick = (template) => {
        setTemplateToDelete(template);
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = async () => {
        if (!templateToDelete) return;

        try {
            await API.deleteTemplate(templateToDelete.id);
            await loadTemplates();
            setShowDeleteConfirm(false);
            setTemplateToDelete(null);
        } catch (err) {
            setError(err.message || 'Erreur lors de la suppression');
            setShowDeleteConfirm(false);
            setTemplateToDelete(null);
            console.error(err);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirm(false);
        setTemplateToDelete(null);
    };

    const handleViewUsages = async (id) => {
        const template = templates.find(t => t.id === id);
        if (template) {
            setSelectedTemplateForUsages(template);
            setShowUsagesModal(true);
        }
    };

    const handleViewVersions = (id) => {
        const template = templates.find(t => t.id === id);
        if (template) {
            setSelectedTemplateForHistory(template);
            setShowVersionHistory(true);
        }
    };

    const handleMenuAction = (templateId, action) => {
        setOpenDropdownId(null);
        switch (action) {
            case 'edit':
                handleEdit(templateId);
                break;
            case 'duplicate':
                handleDuplicate(templateId);
                break;
            case 'usages':
                handleViewUsages(templateId);
                break;
            case 'versions':
                handleViewVersions(templateId);
                break;
            case 'delete':
                const template = templates.find(t => t.id === templateId);
                handleDeleteClick(template);
                break;
            default:
                break;
        }
    };

    // Gérer le clic en dehors du dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdownId(null);
            }
        };

        if (openDropdownId !== null) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [openDropdownId]);

    return (
        <div className="template-library-page">
            <GlobalHeader />

            <div className="template-library-container">
                <div className="list-header">
                    <h1>Bibliothèque de Modèles</h1>
                    <div className="header-actions">
                        <button
                            className="btn-signatures"
                            onClick={() => navigate('/signatures')}
                        >
                            <svg
                                width="20"
                                height="20"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                            </svg>
                            Mes Signatures
                        </button>
                        {canCreate && (
                            <button className="btn-create-new" onClick={handleCreateNew}>
                                <svg
                                    width="20"
                                    height="20"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 4v16m8-8H4"
                                    />
                                </svg>
                                Nouveau Modèle
                            </button>
                        )}
                    </div>
                </div>

                <div className="template-library-toolbar">
                    <div className="search-box">
                        <input
                            type="text"
                            placeholder="Rechercher un modèle..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="view-mode-toggle">
                        <button
                            className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
                            onClick={() => handleViewModeChange('grid')}
                            title="Vue Grille"
                        >
                            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                            </svg>
                        </button>
                        <button
                            className={`view-mode-btn ${viewMode === 'table' ? 'active' : ''}`}
                            onClick={() => handleViewModeChange('table')}
                            title="Vue Tableau"
                        >
                            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                        </button>
                    </div>
                </div>

                {loading && (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Chargement des modèles...</p>
                    </div>
                )}

                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                {!loading && filteredTemplates.length === 0 && (
                    <div className="empty-state">
                        <div className="empty-icon">📄</div>
                        <h3>
                            {searchTerm
                                ? 'Aucun modèle trouvé'
                                : 'Aucun modèle créé'}
                        </h3>
                        {!searchTerm && canCreate && (
                            <button className="btn-primary" onClick={handleCreateNew}>
                                Créer mon premier modèle
                            </button>
                        )}
                    </div>
                )}

                {!loading && filteredTemplates.length > 0 && (
                    <>
                        {viewMode === 'grid' ? (
                            <div className="templates-grid">
                                {filteredTemplates.map(template => (
                            <div key={template.id} className="template-card">
                                <div className="template-card-body">
                                    <TemplateThumbnail
                                        templateId={template.id}
                                        uid={template.uid}
                                        updatedAt={template.updated_at}
                                        containerWidth={template.container_width}
                                        width={300}
                                        height={200}
                                        onClick={() => canUpdate ? handleEdit(template.id) : handleViewUsages(template)}
                                    />

                                    <h3 className="template-name">{template.name}</h3>
                                    {template.description && (
                                        <p className="template-description">{template.description}</p>
                                    )}

                                    <div className="template-meta">
                                        <span>Créé le {new Date(template.created_at).toLocaleDateString('fr-FR')}</span>
                                    </div>

                                    {template.creator_first_name && (
                                        <div className="template-creator">
                                            <svg
                                                width="14"
                                                height="14"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                                />
                                            </svg>
                                            <span className="creator-label">
                                                {template.is_owner ? 'Créé par vous' : `Créé par ${template.creator_first_name} ${template.creator_last_name || ''}`}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="template-card-footer">
                                    <div className="card-actions-menu" ref={openDropdownId === template.id ? dropdownRef : null}>
                                        <button
                                            className="card-btn btn-menu"
                                            onClick={() => setOpenDropdownId(openDropdownId === template.id ? null : template.id)}
                                            title="Actions"
                                        >
                                            <svg
                                                width="16"
                                                height="16"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                                                />
                                            </svg>
                                            Actions
                                        </button>

                                        {openDropdownId === template.id && (
                                            <div className="dropdown-menu">
                                                {canUpdate && (
                                                    <button
                                                        className="dropdown-item dropdown-item-edit"
                                                        onClick={() => handleMenuAction(template.id, 'edit')}
                                                    >
                                                        <svg
                                                            width="16"
                                                            height="16"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                            />
                                                        </svg>
                                                        Éditer
                                                    </button>
                                                )}

                                                {canUpdate && (
                                                    <button
                                                        className="dropdown-item dropdown-item-duplicate"
                                                        onClick={() => handleMenuAction(template.id, 'duplicate')}
                                                    >
                                                        <svg
                                                            width="16"
                                                            height="16"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                            />
                                                        </svg>
                                                        Dupliquer
                                                    </button>
                                                )}

                                                <button
                                                    className="dropdown-item dropdown-item-usages"
                                                    onClick={() => handleMenuAction(template.id, 'usages')}
                                                >
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                        />
                                                    </svg>
                                                    Utilisations
                                                </button>

                                                <button
                                                    className="dropdown-item dropdown-item-versions"
                                                    onClick={() => handleMenuAction(template.id, 'versions')}
                                                >
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                    Historique
                                                </button>

                                                {canDelete && (
                                                    <button
                                                        className="dropdown-item dropdown-item-delete"
                                                        onClick={() => handleMenuAction(template.id, 'delete')}
                                                    >
                                                        <svg
                                                            width="16"
                                                            height="16"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                            />
                                                        </svg>
                                                        Supprimer
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                                ))}
                            </div>
                        ) : (
                            <TemplateTable
                                templates={filteredTemplates}
                                onEdit={handleEdit}
                                onDuplicate={handleDuplicate}
                                onDelete={(id) => {
                                    const template = filteredTemplates.find(t => t.id === id);
                                    handleDeleteClick(template);
                                }}
                                onViewUsages={handleViewUsages}
                                onViewVersions={handleViewVersions}
                                loading={loading}
                                canUpdate={canUpdate}
                                canDelete={canDelete}
                            />
                        )}
                    </>
                )}
            </div>

            {/* Modal de confirmation de suppression */}
            {showDeleteConfirm && (
                <div className="modal-overlay" onClick={handleCancelDelete}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Supprimer le modèle</h3>
                        <p>
                            Êtes-vous sûr de vouloir supprimer le modèle "{templateToDelete?.name}" ?
                        </p>
                        <p className="warning-text">
                            Cette action est irréversible. Le modèle ne pourra être supprimé que s'il n'est utilisé dans aucune signature.
                        </p>
                        <div className="modal-actions">
                            <button className="btn-secondary" onClick={handleCancelDelete}>
                                Annuler
                            </button>
                            <button className="btn-danger" onClick={handleConfirmDelete}>
                                Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal d'historique des versions */}
            <TemplateVersionHistoryModal
                isOpen={showVersionHistory}
                templateId={selectedTemplateForHistory?.id}
                templateName={selectedTemplateForHistory?.name}
                onClose={() => {
                    setShowVersionHistory(false);
                    setSelectedTemplateForHistory(null);
                }}
                onRestore={() => {
                    loadTemplates(); // Recharger la liste après restauration
                }}
                canUpdate={canUpdate}
            />

            {/* Modal des utilisations */}
            <TemplateUsagesModal
                isOpen={showUsagesModal}
                templateId={selectedTemplateForUsages?.id}
                templateName={selectedTemplateForUsages?.name}
                onClose={() => {
                    setShowUsagesModal(false);
                    setSelectedTemplateForUsages(null);
                }}
            />
        </div>
    );
}

export default TemplateLibraryPage;
